import { CardType } from '../../Prototype/Level/Card/CardType';
import {
    DEFAULT_CARD_HEIGHT,
    DEFAULT_CARD_WIDTH,
    ICardData,
} from '../../Prototype/Level/Card/ICardData';
import CardsHelper from '../../Utility/CardsHelper';
import { Random } from '../../Utility/Random';
import { levelGenerationSOFA2 } from './LevelHelper';
import Game from '../../Game';
import { isDynamicAILevel } from '../../State/Level/LevelStateHelper';
import { LevelHelperSettings } from '../../Constants/Level';
import { WorldDataHelper } from '../../Utility/WorldDataHelper';
import { ICardStateData } from '../../State/Card/ICardState';
import { ProgressStateHelper } from 'State/Player/Progress/ProgressStateHelper';

export interface ILevelCardData {
    cardsInPlay: ICardData[];
    cardsInStack: number[];
}

export interface INewCardData {
    cardsInPlay: { [id: string]: ICardStateData };
    cardsInStack: { [id: string]: ICardStateData };
}

export default function LoadCardData(
    levelCardData: ILevelCardData,
    cardsHelper: CardsHelper
): INewCardData {
    levelCardData.cardsInPlay = levelCardData.cardsInPlay.map(
        (card: ICardData) => {
            if (card.type !== CardType.gold) {
                return card;
            }
            return {
                ...card,
                suit: card.suit !== undefined ? card.suit : Random.int(0, 3),
            };
        }
    );
    // Quick clone.
    console.log('Loading card data');
    levelCardData = {
        cardsInPlay: levelCardData.cardsInPlay.map(c => ({ ...c })),
        cardsInStack: [...levelCardData.cardsInStack],
    };

    const newCardData: INewCardData = {
        cardsInPlay: {},
        cardsInStack: {},
    };

    // To minimize the number of generated decks we must first draw all KNOWN cards specified in the level data.
    // Then all RANDOM cards are drawn. This way no KNOWN values are assigned to a RANDOM card which would force
    // a new deck to be generated.

    // Draw known card values in play.
    levelCardData.cardsInPlay
        .filter(c => c.type === CardType.value && !c.random)
        .forEach(c => cardsHelper.draw(c.value));

    const cardsInStack: ICardData[] = new Array(
        levelCardData.cardsInStack.length
    );

    // Draw known card values in stack.
    levelCardData.cardsInStack.forEach((value: number, index: number) => {
        if (value !== -1) {
            cardsInStack[index] = {
                type: CardType.value,
                random: false,
                value: cardsHelper.draw(value),
            };
        }
    });

    const state = Game.store.getState();
    const levelNumber = state.levelState.levelNumber;
    const matchChance: number = Math.min(
        LevelHelperSettings.maxMatchChance,
        LevelHelperSettings.initialMatchChance +
            state.playerState.progress.difficultyReduction *
                LevelHelperSettings.difficultyReductionToMatchChance
    );
    const consecutiveUnsuccessfullAttempts: number = ProgressStateHelper.getLevelData(
        state,
        levelNumber,
        state.levelState.levelType
    ).consecutiveUnsuccessfullAttempts;

    levelGenerationSOFA2({
        outLevelCardData: levelCardData,
        outCardsInStack: cardsInStack,
        cardsHelper,
        levelNumber,
        matchChance,
        isDynamicAILevel: isDynamicAILevel(state),
        consecutiveUnsuccessfullAttempts,
        forceOff:
            levelNumber >=
            WorldDataHelper.getWorldLevelLimits(WorldDataHelper.maxWorld()).min,
    });

    // Sort cardData array for proper initial depth-sorting.
    levelCardData.cardsInPlay.sort((a: any, b: any) => {
        return a.depth - b.depth;
    });

    for (const cardData of levelCardData.cardsInPlay) {
        const card: any = {
            id: cardData.id,
            type: cardData.type,
            random: cardData.random,
            value: cardData.value,
            depth: cardData.depth,
            faceUp: cardData.faceUp,
            sequence: cardData.sequence,
            modifiers: cardData.modifiers,
            x: cardData.x,
            y: cardData.y,
            rotation: (cardData.angle / 180) * Math.PI,
            attempts: 0,
            width: DEFAULT_CARD_WIDTH,
            height: DEFAULT_CARD_HEIGHT,
            suit: cardData.suit,
        };

        newCardData.cardsInPlay[card.id] = card;
    }

    cardsInStack.reverse();
    let stackCardCount = 0;
    for (const cardData of cardsInStack) {
        cardData.id = `stack_card_${++stackCardCount}`;

        const card: any = {
            id: cardData.id,
            type: cardData.type,
            random: cardData.random,
            value: cardData.value,
            depth: 0,
            faceUp: false,
            sequence: null,
            modifiers: [],
            x: -150,
            y: 0,
            rotation: 0,
            attempts: 0,
            width: DEFAULT_CARD_WIDTH,
            height: DEFAULT_CARD_HEIGHT,
        };

        newCardData.cardsInStack[card.id] = card;
    }

    return newCardData;
}
